import * as _ from 'lodash';
import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, IAppState, IWatrRequestLookupDataResponse, IWatrRequestResponse, WatrActionCreators } from '@nkr/store/index';
import { IListItem, IWageAndTravelRequestWeek, IPhysicianLetterWeekUpload, IWatrRequestWeeks } from '@nkr/models/index';
import { AdditionalWeeksReason, DashDonationType, OrganType, WageAndTravelReimbursementRequestStatus } from '@nkr/enums/index';
import { Modal, Button, FormGroup, ControlLabel, FormControl, HelpBlock, Glyphicon } from 'react-bootstrap';
import { getAnchorWeekForSharedComplicationsReport } from '@nkr/services/additional-week-policy';
import { ViewStep, IItem } from './types';
import {
    getAllowedReasonsForItem,
    isReviewSubmittable,
    physicianLettersSatisfiedForItem,
    reasonIsValidForAllWeeks,
    reasonTextValid,
    requiresPhysicianInitialsForItem
} from './selectors';
import RequestListView from './request-list-view';
import ReviewHeader from './review-header';
import ReviewDecisionFields from './review-decision-fields';
import ManualLaborJobSection from './manual-labor-job-section';
import PhysicianLetterSection from './physician-letter-section';
import PolicyFooter from './policy-footer';
import WeekHistoryTable from './week-history-table';

const FORM_SECTION_LABEL_STYLE: React.CSSProperties = {
    fontSize: 11,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#777',
    fontWeight: 600,
    marginBottom: 8
};

const PANEL_OUTLINE_STYLE: React.CSSProperties = {
    marginBottom: 0,
    borderColor: '#dfe3e8',
    borderRadius: 4,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
};

const PANEL_BODY_STYLE: React.CSSProperties = {
    padding: '16px 18px'
};

const PDF_FIELD_LABEL_STYLE: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: '#333',
    marginBottom: 6,
    display: 'block'
};

interface IProps {
    centerId: number,
    show: boolean,
    items: { name: string, alias: string, requestId: number, weeks: number[], status: WageAndTravelReimbursementRequestStatus, organType: DashDonationType, hasActiveComplicationsReport?: boolean }[],
    onSave: (list: IWageAndTravelRequestWeek[], physicianLetters?: IPhysicianLetterWeekUpload[], options?: { stayOpen?: boolean }) => void | Promise<void>,
    onClose: () => void,
    startInReview?: boolean,
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    getRequestLookupData: (centerId: number, isSuperUser: boolean) => Promise<IWatrRequestLookupDataResponse>
    getRequestById: (centerId: number, requestId: number) => Promise<IWatrRequestResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    list: IItem[],
    physicianInitials: string,
    additionalWeeksReasonList: IListItem[],
    sharedComplicationsReportByRequest: { [requestId: string]: File | null },
    viewStep: ViewStep,
    activeRequestId: number | null,
    weekHistory: IWatrRequestWeeks[],
    weekHistoryLoading: boolean,
    weekHistoryWageAmount: number,
    weekHistoryCalculatedWeeklyPayByYear: number,
    weekHistoryEmploymentType: number | string | null,
    weekHistoryIncomePercentCovered: number,
}

class OutstandingReimbursementRequests extends React.Component<TProps, IState> {
    readonly state: IState = {
        list: [],
        physicianInitials: '',
        additionalWeeksReasonList: [],
        sharedComplicationsReportByRequest: {},
        viewStep: 'list',
        activeRequestId: null,
        weekHistory: [],
        weekHistoryLoading: false,
        weekHistoryWageAmount: 0,
        weekHistoryCalculatedWeeklyPayByYear: 0,
        weekHistoryEmploymentType: null,
        weekHistoryIncomePercentCovered: 0,
    }

    private reviewHeadingRef = React.createRef<HTMLDivElement>();

    mapPropsItem = (x: IProps['items'][0]): IItem => ({
        name: x.name,
        alias: x.alias,
        weeks: x.weeks != null ? x.weeks : [],
        requestId: x.requestId,
        status: x.status,
        organType: x.organType,
        hasActiveComplicationsReport: x.hasActiveComplicationsReport === true
    });

    seedListFromItems = (items: IProps['items'], resetReview = false) => {
        const list = items.map(x => this.mapPropsItem(x));
        if (resetReview) {
            this.setState({
                list,
                sharedComplicationsReportByRequest: {},
                physicianInitials: '',
                viewStep: 'list',
                activeRequestId: null,
                weekHistory: [],
                weekHistoryLoading: false,
                weekHistoryWageAmount: 0,
                weekHistoryCalculatedWeeklyPayByYear: 0,
                weekHistoryEmploymentType: null,
                weekHistoryIncomePercentCovered: 0,
            });
        } else {
            this.setState({ list, sharedComplicationsReportByRequest: {} });
        }
    }

    componentDidMount = () => {
        this.getLookupData();
        if (this.props.show && this.props.items && this.props.items.length > 0) {
            this.openFromItems(this.props.items);
        }
    }

    componentDidUpdate = (prevProps: IProps) => {
        if (this.props.show && !prevProps.show) {
            this.openFromItems(this.props.items || []);
            this.getLookupData();
        }
        if (this.props.show && prevProps.show && this.props.items !== prevProps.items) {
            this.openFromItems(this.props.items || []);
        }
    }

    getLookupData = () => {
        if (!this.state.additionalWeeksReasonList || this.state.additionalWeeksReasonList.length === 0) {
            this.props.getRequestLookupData(this.props.centerId, true).then(lookupDataResponse => {
                let { additionalWeeksReasonList } = lookupDataResponse.data.results;

                this.setState({
                    additionalWeeksReasonList
                });
            });
        }
    }

    resetReviewState = (): Pick<IState, 'physicianInitials' | 'sharedComplicationsReportByRequest' | 'viewStep' | 'activeRequestId' | 'weekHistory' | 'weekHistoryLoading' | 'weekHistoryWageAmount' | 'weekHistoryCalculatedWeeklyPayByYear' | 'weekHistoryEmploymentType' | 'weekHistoryIncomePercentCovered'> => ({
        physicianInitials: '',
        sharedComplicationsReportByRequest: {},
        viewStep: 'list',
        activeRequestId: null,
        weekHistory: [],
        weekHistoryLoading: false,
        weekHistoryWageAmount: 0,
        weekHistoryCalculatedWeeklyPayByYear: 0,
        weekHistoryEmploymentType: null,
        weekHistoryIncomePercentCovered: 0,
    })

    openListFromItems = (items: IProps['items']) => {
        this.seedListFromItems(items || [], true);
    }

    openFromItems = (items: IProps['items']) => {
        const nextItems = items || [];
        if (this.props.startInReview && nextItems.length > 0) {
            const list = nextItems.map(x => this.mapPropsItem(x));
            const requestId = nextItems[0].requestId;
            this.setState({
                list,
                sharedComplicationsReportByRequest: {},
                physicianInitials: '',
                viewStep: 'review',
                activeRequestId: requestId,
                weekHistory: [],
                weekHistoryLoading: true,
                weekHistoryWageAmount: 0,
                weekHistoryCalculatedWeeklyPayByYear: 0,
                weekHistoryEmploymentType: null,
                weekHistoryIncomePercentCovered: 0,
            }, () => {
                const el = this.reviewHeadingRef.current;
                if (el) {
                    el.focus();
                }
                this.loadWeekHistory(requestId);
            });
            return;
        }
        this.openListFromItems(nextItems);
    }

    getActiveItem = (): IItem | null => {
        if (this.state.viewStep !== 'review' || this.state.activeRequestId == null) {
            return null;
        }
        return this.state.list.find(i => i.requestId === this.state.activeRequestId) ?? null;
    }

    loadWeekHistory = (requestId: number) => {
        this.setState({
            weekHistoryLoading: true,
            weekHistory: [],
            weekHistoryWageAmount: 0,
            weekHistoryCalculatedWeeklyPayByYear: 0,
            weekHistoryEmploymentType: null,
            weekHistoryIncomePercentCovered: 0,
        });
        this.props.getRequestById(this.props.centerId, requestId)
            .then((response) => {
                if (this.state.activeRequestId !== requestId) {
                    return;
                }
                const results = response && response.data && response.data.results ? response.data.results : [];
                const request = results[0];
                const weeks = request && request.requestWeeks ? request.requestWeeks : [];
                this.setState({
                    weekHistory: weeks,
                    weekHistoryLoading: false,
                    weekHistoryWageAmount: request && request.actualWageReimbursementAmount
                        ? request.actualWageReimbursementAmount
                        : 0,
                    weekHistoryCalculatedWeeklyPayByYear: request && request.calculatedWeeklyPayByYear
                        ? request.calculatedWeeklyPayByYear
                        : 0,
                    weekHistoryEmploymentType: request && request.employmentType != null
                        ? request.employmentType
                        : null,
                    weekHistoryIncomePercentCovered: request && request.incomePercentCovered
                        ? request.incomePercentCovered
                        : 0,
                });
            })
            .catch(() => {
                if (this.state.activeRequestId !== requestId) {
                    return;
                }
                this.setState({
                    weekHistory: [],
                    weekHistoryLoading: false,
                    weekHistoryWageAmount: 0,
                    weekHistoryCalculatedWeeklyPayByYear: 0,
                    weekHistoryEmploymentType: null,
                    weekHistoryIncomePercentCovered: 0,
                });
            });
    }

    handleReviewRequest = (requestId: number) => {
        this.setState({
            viewStep: 'review',
            activeRequestId: requestId,
            physicianInitials: '',
            sharedComplicationsReportByRequest: {},
            weekHistory: [],
            weekHistoryLoading: true,
            weekHistoryWageAmount: 0,
            weekHistoryCalculatedWeeklyPayByYear: 0,
            weekHistoryEmploymentType: null,
            weekHistoryIncomePercentCovered: 0,
        }, () => {
            const el = this.reviewHeadingRef.current;
            if (el) {
                el.focus();
            }
            this.loadWeekHistory(requestId);
        });
    }

    handleBackToList = () => {
        this.openListFromItems(this.props.items || []);
    }

    collectPhysicianLettersForSave = (requestId: number): IPhysicianLetterWeekUpload[] => {
        const item = this.state.list.find(x => x.requestId === requestId);
        if (!item || item.status !== WageAndTravelReimbursementRequestStatus.CenterApproved) {
            return [];
        }
        const anchor = getAnchorWeekForSharedComplicationsReport(item.organType, item.weeks, item.additionalWeeksReason);
        if (anchor == null) {
            return [];
        }
        if (item.hasActiveComplicationsReport) {
            return [];
        }
        const file = this.state.sharedComplicationsReportByRequest[String(requestId)] ?? null;
        if (!file) {
            return [];
        }
        return [{
            requestId: item.requestId,
            week: anchor,
            documentType: 'complicationsReport',
            file,
            attested: true
        }];
    }

    reasonIsValidForAllWeeks = (item: IItem): boolean => {
        return reasonIsValidForAllWeeks(item);
    }

    reasonTextValid = (item: IItem): boolean => {
        return reasonTextValid(item);
    }

    physicianLettersSatisfiedForItem = (item: IItem): boolean => {
        return physicianLettersSatisfiedForItem(item, this.state.sharedComplicationsReportByRequest);
    }

    handleSave = () => {
        const active = this.getActiveItem();
        if (!active) {
            return;
        }
        if (!(active.status > WageAndTravelReimbursementRequestStatus.PendingApproval
            && active.status < WageAndTravelReimbursementRequestStatus.Reimbursed)) {
            return;
        }
        let list: IWageAndTravelRequestWeek[] = [];
        active.weeks.forEach(week => {
            list.push({
                requestId: active.requestId,
                week: week,
                status: active.status,
                additionalWeeksJobDescription: active.additionalWeeksJobDescription,
                additionalWeeksReason: active.additionalWeeksReason,
                additionalWeeksReasonOther: active.additionalWeeksReasonOther,
                additionalWeeksDrSignOff: this.state.physicianInitials,
                policyOrganType: active.organType
            });
        });
        let physicianLetters = this.collectPhysicianLettersForSave(active.requestId);
        const done = this.props.onSave(list, physicianLetters, { stayOpen: true });
        if (done != null && typeof (done as PromiseLike<void>).then === 'function') {
            (done as Promise<void>).then(() => {
                if (!this.props.show) {
                    return;
                }
                this.setState({
                    viewStep: 'list',
                    activeRequestId: null,
                    physicianInitials: '',
                    sharedComplicationsReportByRequest: {},
                });
            });
        }
    }

    handleStatusChange = (id: number, status: WageAndTravelReimbursementRequestStatus, weeks: number[]) => {
        let list = this.state.list.map(x => {
            if (x.requestId == id) {
                return {
                    ...x,
                    status,
                    additionalWeeksReason: undefined,
                    additionalWeeksReasonOther: '',
                    additionalWeeksJobDescription: '',
                };
            }
            return x;
        });
        this.setState({ list });
    }

    handleReasonChange = (id: number, additionalWeeksReason: number | null, weeks: number[]) => {
        let list = this.state.list.map(x => {
            if (x.requestId == id) {
                return {
                    ...x,
                    additionalWeeksReason: additionalWeeksReason === null ? undefined : additionalWeeksReason
                };
            }
            return x;
        });
        this.setState(prev => ({
            list,
            sharedComplicationsReportByRequest: {
                ...prev.sharedComplicationsReportByRequest,
                [String(id)]: null
            }
        }));
    }

    handleReasonOtherChange = (id: number, additionalWeeksReasonOther: string, weeks: number[]) => {
        let list = this.state.list.map(x => {
            if (x.requestId == id) {
                return { ...x, additionalWeeksReasonOther };
            }
            return x;
        });
        this.setState({ list });
    }

    handleJobDescriptionChange = (id: number, additionalWeeksJobDescription: string, weeks: number[]) => {
        let list = this.state.list.map(x => {
            if (x.requestId == id) {
                return { ...x, additionalWeeksJobDescription };
            }
            return x;
        });
        this.setState({ list });
    }

    handleInitialsChanged = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let value = e.currentTarget.value;
        let alphaValue = value.replace(/[^A-Za-z]/g, '');

        this.setState({
            physicianInitials: alphaValue.toUpperCase()
        });
    }

    handleSharedComplicationsFile = (requestId: number, e: React.FormEvent<HTMLInputElement>) => {
        const input = e.currentTarget;
        const file = input.files && input.files.length > 0 ? input.files[0] : null;
        this.setState(prev => ({
            sharedComplicationsReportByRequest: {
                ...prev.sharedComplicationsReportByRequest,
                [String(requestId)]: file
            }
        }));
    }

    showInitialFormForItem = (item: IItem | null): boolean => {
        if (!item) {
            return false;
        }
        return item.status == WageAndTravelReimbursementRequestStatus.CenterApproved;
    }

    requiresPhysicianInitialsForItem = (item: IItem): boolean => {
        return requiresPhysicianInitialsForItem(item);
    }

    checkValidReview = (): boolean => {
        return isReviewSubmittable(
            this.getActiveItem(),
            this.state.physicianInitials,
            this.state.sharedComplicationsReportByRequest
        );
    }

    handleCancel = (): void => {
        this.setState(this.resetReviewState());

        this.props.onClose();
    }

    handleOpenLink = (link: string) => {
        window.open(link, '_blank');
    };

    getCenterApprovedManualLaborItems = (requestId: number | null): IItem[] => {
        return this.state.list.filter(
            item =>
                item.requestId === requestId
                && item.status === WageAndTravelReimbursementRequestStatus.CenterApproved
                && item.additionalWeeksReason === AdditionalWeeksReason.ManualLabor
        );
    }

    renderPhysicianLetterSectionForItem = (item: IItem, embedded?: boolean) => {
        if (item.status !== WageAndTravelReimbursementRequestStatus.CenterApproved) {
            return null;
        }
        const anchor = getAnchorWeekForSharedComplicationsReport(item.organType, item.weeks, item.additionalWeeksReason);
        if (anchor == null) {
            return null;
        }
        const rid = item.requestId;
        const file = this.state.sharedComplicationsReportByRequest[String(rid)] ?? null;
        return (
            <PhysicianLetterSection
                item={item}
                panelOutlineStyle={PANEL_OUTLINE_STYLE}
                panelBodyStyle={PANEL_BODY_STYLE}
                pdfFieldLabelStyle={PDF_FIELD_LABEL_STYLE}
                file={file}
                onPick={(e) => this.handleSharedComplicationsFile(rid, e)}
                embedded={embedded === true}
            />
        );
    }

    renderReviewDecisionFields = (activeItem: IItem) => {
        const { requiresReason, reasons, reasonMissing } = getAllowedReasonsForItem(activeItem, this.state.additionalWeeksReasonList);
        return (
            <ReviewDecisionFields
                activeItem={activeItem}
                reasons={reasons}
                requiresReason={requiresReason}
                reasonMissing={reasonMissing}
                formSectionLabelStyle={FORM_SECTION_LABEL_STYLE}
                onStatusChange={this.handleStatusChange}
                onReasonChange={this.handleReasonChange}
                onReasonOtherChange={this.handleReasonOtherChange}
            />
        );
    }

    renderReviewBody = (activeItem: IItem) => {
        const rid = activeItem.requestId;
        const hasManualLaborFields = this.getCenterApprovedManualLaborItems(rid).length > 0;
        const showInitials = this.requiresPhysicianInitialsForItem(activeItem);
        const pdfPanel = this.renderPhysicianLetterSectionForItem(activeItem, true);
        const showApprovedDetails = this.showInitialFormForItem(activeItem)
            && (hasManualLaborFields || showInitials || !!pdfPanel);
        const sectionRule: React.CSSProperties = {
            marginTop: 16,
            paddingTop: 16,
            borderTop: '1px solid #e9ecef'
        };

        return (
            <>
                <ReviewHeader
                    activeItem={activeItem}
                    reviewHeadingRef={this.reviewHeadingRef}
                    onBackToList={this.props.startInReview ? undefined : this.handleBackToList}
                />
                <WeekHistoryTable
                    weeks={this.state.weekHistory}
                    pendingWeeks={activeItem.weeks}
                    loading={this.state.weekHistoryLoading}
                    actualWageReimbursementAmount={this.state.weekHistoryWageAmount}
                    calculatedWeeklyPayByYear={this.state.weekHistoryCalculatedWeeklyPayByYear}
                    employmentType={this.state.weekHistoryEmploymentType}
                    incomePercentCovered={this.state.weekHistoryIncomePercentCovered}
                />
                {this.renderReviewDecisionFields(activeItem)}
                {showApprovedDetails && (
                    <div className='panel panel-default' style={{ ...PANEL_OUTLINE_STYLE, marginTop: 0 }}>
                        <div className='panel-heading' style={{ fontSize: 13, fontWeight: 600 }}>
                            Additional approval details
                        </div>
                        <div className='panel-body' style={{ ...PANEL_BODY_STYLE, paddingTop: 14 }}>
                            {hasManualLaborFields && (
                                <ManualLaborJobSection
                                    rows={this.getCenterApprovedManualLaborItems(rid)}
                                    formSectionLabelStyle={FORM_SECTION_LABEL_STYLE}
                                    onJobDescriptionChange={this.handleJobDescriptionChange}
                                />
                            )}
                            {showInitials && (
                                <FormGroup style={{ marginBottom: 0, ...(hasManualLaborFields ? sectionRule : {}) }}>
                                    <ControlLabel style={FORM_SECTION_LABEL_STYLE}>Authorizing physician initials</ControlLabel>
                                    <FormControl
                                        type='text'
                                        value={this.state.physicianInitials}
                                        maxLength={5}
                                        placeholder='Physician initials'
                                        bsSize='sm'
                                        onChange={this.handleInitialsChanged}
                                        style={{ maxWidth: 320, borderRadius: 3 }}
                                    />
                                    <HelpBlock className='text-muted' style={{ marginBottom: 0, fontSize: 12 }}>
                                        At least two characters required.
                                    </HelpBlock>
                                </FormGroup>
                            )}
                            {pdfPanel && (
                                <div style={(hasManualLaborFields || showInitials) ? sectionRule : {}}>
                                    {pdfPanel}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </>
        );
    }

    render = () => {
        const activeItem = this.getActiveItem();
        const disableSubmit = this.state.viewStep === 'review' ? !this.checkValidReview() : true;

        return (
            <Modal
                show={this.props.show}
                bsSize='lg'
                backdrop='static'
                onHide={this.handleCancel}
                className='modal-vertical-center'
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {this.props.startInReview
                            ? 'Outstanding Reimbursement Request'
                            : 'TO-DO - Outstanding Reimbursement Requests'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!this.props.startInReview && this.state.viewStep === 'list' && (
                        <RequestListView list={this.state.list} onReviewRequest={this.handleReviewRequest} />
                    )}
                    {this.state.viewStep === 'review' && activeItem && this.renderReviewBody(activeItem)}
                </Modal.Body>
                <Modal.Footer
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 12,
                    }}
                >
                    <PolicyFooter
                        isKidney={this.props.app.site.organType == OrganType.Kidney}
                        onOpenLink={this.handleOpenLink}
                    />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {this.state.viewStep === 'review' && (
                            <Button bsStyle='primary' bsSize='sm' disabled={disableSubmit} onClick={this.handleSave}>
                                Submit
                            </Button>
                        )}
                        <Button bsSize='sm' onClick={this.handleCancel}>Cancel</Button>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        getRequestLookupData: () => {
            return dispatch(WatrActionCreators.getLookupData());
        },
        getRequestById: (centerId: number, requestId: number) => {
            return dispatch(WatrActionCreators.getRequestById(centerId, requestId));
        }
    }
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(OutstandingReimbursementRequests);
